import { FabricObject, Textbox } from 'fabric';
import {
  applyTextStyleRules,
  autoFitTextbox,
  setTextPreservingStyles,
  type AutoFitConfig
} from '@doer/frontend-components/creativity-editor';
import type { Effect } from './effect';
import { readObjectConfig } from '../core/object-config';

const getAutoFitConfig = (obj: FabricObject): AutoFitConfig | undefined =>
  readObjectConfig<AutoFitConfig>(
    obj,
    'autoFit',
    c => typeof c.minFontSize === 'number' && typeof c.maxFontSize === 'number' && typeof c.maxHeight === 'number'
  );

type StyleRange = { start: number; end: number; style: Record<string, unknown> };

function extendTrailingStyleRange(styles: StyleRange[], oldLength: number, newLength: number): StyleRange[] {
  if (newLength <= oldLength) return styles;
  const idx = styles.findIndex(s => s.end === oldLength);
  if (idx === -1) return styles;
  return styles.map((s, i) => (i === idx ? { ...s, end: newLength } : s));
}

export const textEffect: Effect = {
  applyToJson(obj, value) {
    if (value == null) return null;
    const newText = String(value);
    const oldText = typeof obj.text === 'string' ? obj.text : '';
    const styles = Array.isArray(obj.styles)
      ? extendTrailingStyleRange(obj.styles as StyleRange[], oldText.length, newText.length)
      : obj.styles;
    return { ...obj, text: newText, styles };
  },

  async applyToCanvas(obj, value) {
    if (value == null) return;

    const newText = String(value);
    if (!(obj instanceof Textbox)) {
      obj.set('text', newText);
      return;
    }
    // Idempotency: skip text mutation + autoFit when nothing changed.
    if (obj.text === newText) return;

    setTextPreservingStyles(obj, newText);
    // textStyleRules can change char widths (bold), so apply BEFORE autoFit measures.
    applyTextStyleRules(obj);
    const cfg = getAutoFitConfig(obj);
    if (cfg) autoFitTextbox(obj, cfg);
  }
};
